import { arrayRemove, arrayUnion, Timestamp } from 'firebase/firestore'
import {
  and,
  collection,
  deleteField,
  doc,
  or,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore'
import {
  convertDocumentSnapshotToModel,
  modelItemStream,
  modelListStream,
  partitionedQueryStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { Section } from '../../models/Section'
import type { ImpersonationInfo, RoomStateVideoMethod } from '../../types'
import { SectionState } from '../../types'
import type { FirestoreSection } from './schema'
import { SectionPaymentMethod } from './schema'
import { schema, writeSchema } from './schema'
import {
  addDocWithError,
  deleteDocWithError,
  getDocsWithError,
  getDocWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'
import type {
  FieldValue,
  CollectionReference,
  DocumentData,
  DocumentReference,
  PartialWithFieldValue,
  Firestore,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  QuerySnapshot,
} from 'firebase/firestore'
import { partition } from '../../util/arrays'
import type { DateTime } from 'luxon'
import type { FirestoreSectionProduct } from '../SectionProduct/schema'
import { getColRef as getSectionProductColRef } from '../SectionProduct'
import { StreamController } from 'tricklejs'
import { collectionSnapshots } from '../../firestore-mobx/stream'
import { createBatchGet } from '../../util/batch-get'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreSection> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreSection>) => {
    writeSchema.partial().parse(data)

    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore
): CollectionReference<FirestoreSection> => {
  return collection(firestore, 'section').withConverter(converter)
}

const getDocRef = (
  firestore: Firestore,
  { sectionId }: { sectionId: string }
): DocumentReference<FirestoreSection, DocumentData> => {
  return doc(getColRef(firestore), sectionId)
}

export const fetchAllSections = async (repository: FirebaseRepository) => {
  const ref = getColRef(repository.firestore)
  const snapshot = await getDocsWithError(ref, 'FetchAllSectionsError')
  return snapshot.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, Section)
  )
}

export const getSections = (
  repository: FirebaseRepository,
  impersonationInfo?: ImpersonationInfo
) => {
  const q = query(
    getColRef(repository.firestore),
    and(
      or(
        where(
          'userIds',
          'array-contains',
          impersonationInfo?.userId ?? repository.uid
        ),
        where(
          'instructorUserId',
          '==',
          impersonationInfo?.userId ?? repository.uid
        )
      ),
      where('sectionState', '>=', 0)
    )
  )
  return modelListStream(repository, q, Section)
}

export const fetchSectionsForUser = async (
  repository: FirebaseRepository,
  {
    userId,
  }: {
    userId?: string
  }
) => {
  const q = query(
    getColRef(repository.firestore),
    where('userIds', 'array-contains', userId || repository.uid)
  )
  const snapshot = await getDocsWithError(q, 'FetchSectionsForUserError')

  return snapshot.docs.map((doc) => {
    return convertDocumentSnapshotToModel(repository, doc, Section)
  })
}

export const getSectionsStreamForInstructor = (
  repository: FirebaseRepository,
  impersonationInfo?: ImpersonationInfo
) => {
  const ref = getColRef(repository.firestore)
  const userId = impersonationInfo?.userId || repository.uid

  const q = query(
    ref,
    where('instructorUserId', '==', userId),
    where('sectionState', '>=', 0)
  )

  return modelListStream(repository, q, Section)
}

export const getSectionsStreamForInstructors = (
  repository: FirebaseRepository,
  instructorIds: string[]
) => {
  return partitionedQueryStream({
    repository,
    ref: getColRef(repository.firestore),
    matchArray: instructorIds,
    model: Section,
    queryBuilder: (ref, ids) =>
      query(
        ref,
        where('instructorUserId', 'in', ids),
        where('sectionState', '>=', 0)
      ),
  })
}

export const fetchSectionsForInstructors = async (
  repository: FirebaseRepository,
  { instructorIds }: { instructorIds: string[] }
) => {
  if (!instructorIds.length) return []
  const ref = getColRef(repository.firestore)
  const parts = partition(instructorIds, 15)
  const sections = await Promise.all(
    parts.map(async (part) => {
      const q = query(ref, where('instructorUserId', 'in', part))
      const snapshot = await getDocsWithError(
        q,
        'FetchSectionsForInstructorsError'
      )
      return snapshot.docs.map((doc) =>
        convertDocumentSnapshotToModel(repository, doc, Section)
      )
    })
  )
  return sections.flat()
}

export const fetchInstructorSections = async (
  repository: FirebaseRepository,
  {
    instructorUserId,
  }: {
    instructorUserId?: string
  }
) => {
  const q = query(
    getColRef(repository.firestore),
    where('instructorUserId', '==', instructorUserId || repository.uid),
    where('sectionState', '>=', 0)
  )

  const snapshot = await getDocsWithError(q, 'FetchInstructorSectionsError')

  return snapshot.docs.map((doc) => {
    return convertDocumentSnapshotToModel(repository, doc, Section)
  })
}

export const getSection = (
  repository: FirebaseRepository,
  { sectionId }: { sectionId: string }
) => {
  const ref = getColRef(repository.firestore)
  const docRef = doc(ref, sectionId)

  return modelItemStream(repository, docRef, Section)
}

export const getSectionsById = (
  repository: FirebaseRepository,
  sectionIds: string[]
) => {
  return partitionedQueryStream({
    repository,
    ref: getColRef(repository.firestore),
    matchArray: sectionIds,
    model: Section,
  })
}

export const fetchSectionsByIds = async (
  repository: FirebaseRepository,
  sectionIds: string[]
) => {
  if (!sectionIds.length) return []

  const ref = getColRef(repository.firestore)

  return createBatchGet({
    repository,
    collectionRef: ref,
    ids: sectionIds,
    modelClass: Section,
  })
}

export const fetchSection = async (
  repository: FirebaseRepository,
  { sectionId }: { sectionId: string }
) => {
  const ref = getColRef(repository.firestore)

  const docRef = doc(ref, sectionId)

  const snapshot = await getDocWithError(docRef, 'FetchSectionError')

  return convertDocumentSnapshotToModel(repository, snapshot, Section)
}

export const createSection = (
  repository: FirebaseRepository,
  {
    className,
    sectionName,
    userId,
    organizationId,
    sectionPaymentMethod,
    sectionPassPrice,
    expectedStudents,
    sectionStartDate,
    sectionEndDate,
    isTest,
  }: {
    userId: string
    className: string
    sectionName: string
    organizationId?: string
    sectionPaymentMethod: SectionPaymentMethod
    sectionPassPrice?: number
    expectedStudents?: number
    sectionStartDate?: DateTime
    sectionEndDate?: DateTime
    isTest?: boolean
  }
) => {
  const payload: Omit<
    FirestoreSection,
    'updatedAt' | 'sectionPassPrice' | 'sectionStartDate' | 'sectionEndDate'
  > & {
    updatedAt: FieldValue
    sectionPassPrice?: number | FieldValue
    sectionStartDate: Timestamp
    sectionEndDate: Timestamp
  } = {
    className,
    sectionName,
    instructorUserId: userId,
    userIds: [],
    slideDeckIds: [],
    sectionState: SectionState.notStarted,
    updatedAt: serverTimestamp(),
    sectionPaymentMethod: sectionPaymentMethod,
    sectionStartDate: sectionStartDate
      ? Timestamp.fromDate(sectionStartDate.toJSDate())
      : Timestamp.now(),
    sectionEndDate: sectionEndDate
      ? Timestamp.fromDate(sectionEndDate.toJSDate())
      : Timestamp.now(),
    collaboratorRWUserIds: [],
  }

  if (
    [
      SectionPaymentMethod.sectionPassStripe,
      SectionPaymentMethod.sectionPassStripeCoupon,
    ].includes(sectionPaymentMethod) &&
    sectionPassPrice
  ) {
    payload.sectionPassPrice = sectionPassPrice
  }

  if (organizationId) {
    payload.organizationId = organizationId
  }

  if (expectedStudents !== undefined)
    payload.expectedStudents = expectedStudents

  if (isTest !== undefined) payload.isTest = isTest

  return addDocWithError(
    getColRef(repository.firestore),
    payload,
    'CreateSectionError'
  )
}

export const createSectionWithProduct = async (
  repository: FirebaseRepository,
  {
    className,
    sectionName,
    currentUserId,
    instructorUserId,
    organizationId,
    sectionPaymentMethod,
    sectionPassPrice,
    expectedStudents,
    sectionStartDate,
    sectionEndDate,
    productId,
    customProduct,
    isTest,
  }: {
    currentUserId: string
    instructorUserId: string
    className: string
    sectionName: string
    organizationId?: string
    sectionPaymentMethod: SectionPaymentMethod
    sectionPassPrice?: number
    expectedStudents?: number
    sectionStartDate?: DateTime
    sectionEndDate?: DateTime
    productId: string
    customProduct: boolean
    isTest?: boolean
  }
) => {
  return runTransaction(repository.firestore, async (transaction) => {
    // Create section
    const sectionRef = doc(getColRef(repository.firestore))
    const sectionPayload: Omit<
      FirestoreSection,
      'updatedAt' | 'sectionPassPrice' | 'sectionStartDate' | 'sectionEndDate'
    > & {
      updatedAt: FieldValue
      sectionPassPrice?: number | FieldValue
      sectionStartDate: Timestamp
      sectionEndDate: Timestamp
    } = {
      className,
      sectionName,
      instructorUserId,
      userIds: [],
      slideDeckIds: [],
      sectionState: SectionState.notStarted,
      updatedAt: serverTimestamp(),
      sectionPaymentMethod: sectionPaymentMethod,
      sectionStartDate: sectionStartDate
        ? Timestamp.fromDate(sectionStartDate.toJSDate())
        : Timestamp.now(),
      sectionEndDate: sectionEndDate
        ? Timestamp.fromDate(sectionEndDate.toJSDate())
        : Timestamp.now(),
      collaboratorRWUserIds: [],
    }

    if (
      [
        SectionPaymentMethod.sectionPassStripe,
        SectionPaymentMethod.sectionPassStripeCoupon,
      ].includes(sectionPaymentMethod) &&
      sectionPassPrice
    ) {
      sectionPayload.sectionPassPrice = sectionPassPrice
    }

    if (organizationId) {
      sectionPayload.organizationId = organizationId
    }

    if (expectedStudents !== undefined)
      sectionPayload.expectedStudents = expectedStudents

    if (isTest !== undefined) sectionPayload.isTest = isTest

    transaction.set(sectionRef, sectionPayload)

    const sectionProductRef = doc(
      getSectionProductColRef(repository.firestore, sectionRef.id)
    )
    const sectionProductPayload: Omit<FirestoreSectionProduct, 'updatedAt'> & {
      updatedAt: FieldValue
    } = {
      productId,
      sectionId: sectionRef.id,
      customProduct,
      userId: currentUserId,
      updatedAt: serverTimestamp(),
    }

    transaction.set(sectionProductRef, sectionProductPayload)
    return sectionRef.id
  })
}

export const updateSection = (
  repository: FirebaseRepository,
  sectionId: string,
  {
    className,
    sectionName,
    organizationId,
    sectionPaymentMethod,
    sectionPassPrice,
    expectedStudents,
    sectionStartDate,
    sectionEndDate,
    peerReviewExcludesSelfReview,
    isTest,
  }: {
    className?: string
    sectionName?: string
    organizationId?: string
    sectionPaymentMethod?: SectionPaymentMethod
    sectionPassPrice?: number
    expectedStudents?: number
    sectionStartDate?: DateTime
    sectionEndDate?: DateTime
    peerReviewExcludesSelfReview?: boolean
    isTest?: boolean
  }
) => {
  const payload: {
    className?: string
    sectionName?: string
    organizationId?: string | FieldValue
    updatedAt: FieldValue
    sectionPaymentMethod?: SectionPaymentMethod
    sectionPassPrice?: number | FieldValue
    expectedStudents?: number
    sectionStartDate?: Date
    sectionEndDate?: Date
    peerReviewExcludesSelfReview?: boolean
    isTest?: boolean
  } = {
    className,
    sectionName,
    updatedAt: serverTimestamp(),
  }

  if (sectionStartDate) {
    payload.sectionStartDate = sectionStartDate.toJSDate()
  }

  if (sectionEndDate) {
    payload.sectionEndDate = sectionEndDate.toJSDate()
  }

  if (sectionPaymentMethod !== undefined) {
    payload.sectionPaymentMethod = sectionPaymentMethod
    if (
      ![
        SectionPaymentMethod.sectionPassStripe,
        SectionPaymentMethod.sectionPassStripeCoupon,
      ].includes(sectionPaymentMethod)
    ) {
      payload.sectionPassPrice = deleteField()
    } else if (sectionPassPrice) {
      payload.sectionPassPrice = sectionPassPrice
    }
  }

  if (organizationId === '') {
    payload.organizationId = deleteField()
  } else if (organizationId) {
    payload.organizationId = organizationId
  }

  if (expectedStudents !== undefined)
    payload.expectedStudents = expectedStudents

  if (peerReviewExcludesSelfReview !== undefined)
    payload.peerReviewExcludesSelfReview = peerReviewExcludesSelfReview

  if (isTest !== undefined) payload.isTest = isTest

  const docRef = getDocRef(repository.firestore, { sectionId })
  return updateDocWithError(docRef, payload, 'UpdateSectionError')
}

export const updateSectionDealDetails = async (
  repository: FirebaseRepository,
  {
    sectionId,
    hubspotDealId,
    sectionDealExpiresAt,
  }: {
    sectionId: string
    hubspotDealId?: string | null
    sectionDealExpiresAt?: Timestamp | null
  }
) => {
  const payload: {
    hubspotDealId?: string | FieldValue
    sectionDealExpiresAt?: Timestamp | FieldValue
    updatedAt: FieldValue
  } = {
    updatedAt: serverTimestamp(),
  }

  if (hubspotDealId === null) {
    payload.hubspotDealId = deleteField()
  } else if (hubspotDealId) {
    payload.hubspotDealId = hubspotDealId
  }

  if (sectionDealExpiresAt === null) {
    payload.sectionDealExpiresAt = deleteField()
  } else if (sectionDealExpiresAt) {
    payload.sectionDealExpiresAt = sectionDealExpiresAt
  }

  const docRef = getDocRef(repository.firestore, { sectionId })
  return updateDocWithError(docRef, payload, 'UpdateSectionDealDetailsError')
}

/**
 * Sets (or clears, when passed null) the "free access window" end date for a
 * section. While set and in the future, students may access materials without
 * paying (BL-4581).
 */
export const updateSectionFreeAccessUntil = async (
  repository: FirebaseRepository,
  {
    sectionId,
    freeAccessUntil,
  }: {
    sectionId: string
    freeAccessUntil: DateTime | null
  }
) => {
  const payload: {
    freeAccessUntil: Timestamp | FieldValue
    updatedAt: FieldValue
  } = {
    freeAccessUntil:
      freeAccessUntil === null
        ? deleteField()
        : Timestamp.fromDate(freeAccessUntil.toJSDate()),
    updatedAt: serverTimestamp(),
  }

  const docRef = getDocRef(repository.firestore, { sectionId })
  return updateDocWithError(
    docRef,
    payload,
    'UpdateSectionFreeAccessUntilError'
  )
}

export const touchSection = (
  repository: FirebaseRepository,
  sectionId: string
) => {
  const payload = {
    updatedAt: serverTimestamp(),
  }
  const docRef = getDocRef(repository.firestore, { sectionId })
  return updateDocWithError(docRef, payload, 'TouchSectionError')
}

export async function setSectionState(
  repository: FirebaseRepository,
  params: {
    sectionId: string
    sectionState: SectionState
  }
) {
  const colRef = getColRef(repository.firestore)

  const docRef = doc(colRef, params.sectionId)

  return updateDocWithError(
    docRef,
    {
      sectionState: params.sectionState,
      updatedAt: serverTimestamp(),
    },
    'SetSectionStateError'
  )
}

export async function setSectionInvoiced(
  repository: FirebaseRepository,
  params: {
    sectionId: string
  }
) {
  const colRef = getColRef(repository.firestore)

  const docRef = doc(colRef, params.sectionId)

  return updateDocWithError(
    docRef,
    {
      invoiced: true,
      updatedAt: serverTimestamp(),
    },
    'SetSectionInvoicedError'
  )
}

export async function setSectionNotInvoiced(
  repository: FirebaseRepository,
  params: {
    sectionId: string
  }
) {
  const colRef = getColRef(repository.firestore)

  const docRef = doc(colRef, params.sectionId)

  return updateDocWithError(
    docRef,
    {
      invoiced: false,
      updatedAt: serverTimestamp(),
    },
    'SetSectionInvoicedError'
  )
}

export async function setSectionIsTest(
  repository: FirebaseRepository,
  params: { sectionId: string; isTest: boolean }
) {
  const colRef = getColRef(repository.firestore)
  const docRef = doc(colRef, params.sectionId)
  return updateDocWithError(
    docRef,
    { isTest: params.isTest, updatedAt: serverTimestamp() },
    'SetSectionIsTestError'
  )
}

export function getSharedSections(repository: FirebaseRepository) {
  const ref = getColRef(repository.firestore)
  const q = query(ref, where('shareable', '==', true))
  return modelListStream(repository, q, Section)
}

export const getCodelessAccessSections = (repository: FirebaseRepository) => {
  const ref = getColRef(repository.firestore)
  const q = query(
    ref,
    and(
      where(
        'sectionPaymentMethod',
        '==',
        SectionPaymentMethod.sectionPassCodelessAccess
      ),
      where('sectionState', '<', SectionState.completed)
    )
  )
  return modelListStream(repository, q, Section)
}

export const getSectionsByOrganizationId = (
  repository: FirebaseRepository,
  organizationId: string
) => {
  const ref = getColRef(repository.firestore)

  const q = query(ref, where('organizationId', '==', organizationId))

  return modelListStream(repository, q, Section)
}

export const setSectionPassPriceForSection = async (
  repository: FirebaseRepository,
  {
    sectionId,
    priceInCents,
  }: {
    sectionId: string
    priceInCents: number | null
  }
) => {
  const docRef = getDocRef(repository.firestore, { sectionId })
  updateDocWithError(
    docRef,
    {
      sectionPassPrice:
        priceInCents !== null && priceInCents >= 100
          ? priceInCents
          : deleteField(),
      updatedAt: serverTimestamp(),
    },
    'setSectionPassPriceForSection'
  )
}

export const setSectionPassQuantityForSection = async (
  repository: FirebaseRepository,
  {
    sectionId,
    quantity,
  }: {
    sectionId: string
    quantity: number | null
  }
) => {
  const docRef = getDocRef(repository.firestore, { sectionId })
  updateDocWithError(
    docRef,
    {
      sectionPassQuantity: quantity !== null ? quantity : deleteField(),
      updatedAt: serverTimestamp(),
    },
    'setSectionPassQuantityForSection'
  )
}

export const updateSectionVideoMethod = async (
  repository: FirebaseRepository,
  {
    sectionId,
    videoMethod,
  }: {
    sectionId: string
    videoMethod: RoomStateVideoMethod | null
  }
) => {
  const ref = getDocRef(repository.firestore, { sectionId })
  return await updateDocWithError(
    ref,
    {
      videoMethod: videoMethod || deleteField(),
    },
    'updateSectionVideoMethodError'
  )
}

export const updateSectionPaymentMethod = async (
  repository: FirebaseRepository,
  {
    sectionId,
    sectionPaymentMethod,
  }: {
    sectionId: string
    sectionPaymentMethod: SectionPaymentMethod
  }
) => {
  const docRef = getDocRef(repository.firestore, { sectionId })
  return updateDocWithError(
    docRef,
    {
      sectionPaymentMethod,
      updatedAt: serverTimestamp(),
    },
    'UpdateSectionPaymentMethodError'
  )
}

export const deleteSection = async (
  repository: FirebaseRepository,
  sectionId: string
) => {
  const docRef = getDocRef(repository.firestore, { sectionId })
  return deleteDocWithError(docRef, 'DeleteSectionError')
}

export const removeStudentFromSection = async (
  repository: FirebaseRepository,
  sectionId: string,
  studentId: string
) => {
  const docRef = getDocRef(repository.firestore, { sectionId })
  return updateDocWithError(
    docRef,
    {
      userIds: arrayRemove(studentId),
      removedUserIds: arrayUnion(studentId),
      updatedAt: serverTimestamp(),
    },
    'RemoveStudentFromSectionError'
  )
}

export const restoreStudentToSection = async (
  repository: FirebaseRepository,
  sectionId: string,
  studentId: string
) => {
  const docRef = getDocRef(repository.firestore, { sectionId })
  return updateDocWithError(
    docRef,
    {
      userIds: arrayUnion(studentId),
      removedUserIds: arrayRemove(studentId),
      updatedAt: serverTimestamp(),
    },
    'RestoreStudentToSectionError'
  )
}

export const updateSectionCollaborators = async (
  repository: FirebaseRepository,
  sectionId: string,
  assistantUserId: string,
  action: 'add' | 'remove'
) => {
  const docRef = getDocRef(repository.firestore, { sectionId })
  return updateDocWithError(
    docRef,
    {
      collaboratorRWUserIds:
        action === 'add'
          ? arrayUnion(assistantUserId)
          : arrayRemove(assistantUserId),
      updatedAt: serverTimestamp(),
    },
    'UpdateSectionCollaboratorsError'
  )
}

export const batchUpdateSectionCollaborators = async (
  repository: FirebaseRepository,
  updates: Array<{
    sectionId: string
    assistantUserId: string
    action: 'add' | 'remove'
  }>
) => {
  const batch = writeBatch(repository.firestore)

  for (const { sectionId, assistantUserId, action } of updates) {
    const docRef = getDocRef(repository.firestore, { sectionId })
    batch.update(docRef, {
      collaboratorRWUserIds:
        action === 'add'
          ? arrayUnion(assistantUserId)
          : arrayRemove(assistantUserId),
      updatedAt: serverTimestamp(),
    })
  }

  return batch.commit()
}

export const getSectionsStreamForCollaborator = (
  repository: FirebaseRepository,
  userId: string
) => {
  const ref = getColRef(repository.firestore)
  const q = query(ref, where('collaboratorRWUserIds', 'array-contains', userId))
  return modelListStream(repository, q, Section)
}

export const getCollaboratorUserIdsStream = (
  repository: FirebaseRepository,
  impersonationInfo?: ImpersonationInfo
) => {
  const ref = getColRef(repository.firestore)
  const q = query(
    ref,
    where(
      'collaboratorRWUserIds',
      'array-contains',
      impersonationInfo?.userId ?? repository.uid
    )
  )

  const stream = new StreamController<string[]>()
  let cancelled = false

  const subscription = collectionSnapshots(q).listen(
    (snapshot: QuerySnapshot<FirestoreSection>) => {
      if (cancelled) return

      const instructorIds = new Set<string>()
      snapshot.docs.forEach((doc: QueryDocumentSnapshot<FirestoreSection>) => {
        const data = doc.data()
        if (data.instructorUserId) {
          instructorIds.add(data.instructorUserId)
        }
      })

      stream.add(Array.from(instructorIds))
    }
  )

  stream.onCancel = () => {
    cancelled = true
    subscription.cancel()
    stream.close()
  }

  return stream.stream
}

export const removeCollaboratorFromInstructor = async (
  repository: FirebaseRepository,
  collaboratorUserId: string,
  instructorUserId: string
): Promise<void> => {
  const sectionsCollection = getColRef(repository.firestore)

  const q = query(
    sectionsCollection,
    where('instructorUserId', '==', instructorUserId),
    where('collaboratorRWUserIds', 'array-contains', collaboratorUserId)
  )

  const querySnapshot = await getDocsWithError(q)

  if (querySnapshot.empty) {
    return
  }

  const batch = writeBatch(repository.firestore)

  querySnapshot.forEach((doc) => {
    batch.update(doc.ref, {
      collaboratorRWUserIds: arrayRemove(collaboratorUserId),
    })
  })

  await batch.commit()
}
